// src/lib/supabase.ts (or your current file)
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

/**
 * Supabase client
 * - Auth: persisted, auto-refresh, PKCE in browser
 * - Realtime: modest event rate + extended heartbeat for dev
 * - Global headers: useful for tracing
 */
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    headers: {
      'x-client-info': 'edgeai-web/1.0',
    },
  },
  realtime: {
    params: {
      // throttle server push if you have many tabs
      eventsPerSecond: 10,
    },
    // dev-friendly: keep socket alive longer if you reload often
    heartbeatIntervalMs: 28_000,
  },
});

/* ======================================================================================
 * Realtime helpers (optional but convenient)
 * ====================================================================================*/

/**
 * Subscribe to new messages for a given group.
 * Returns an unsubscribe function.
 */
export function listenToGroupMessages(
  groupId: number,
  onInsert: (row: any) => void
) {
  const channel = supabase
    .channel(`grp:${groupId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'study_group_messages',
        filter: `group_id=eq.${groupId}`,
      },
      (payload) => onInsert(payload.new)
    )
    .subscribe();

  return () => supabase.removeChannel(channel);
}

/**
 * Presence channel: track who’s online in a group.
 * - Call `joinPresence(...).then(({channel}) => ...)`
 * - Use `channel.presenceState()` inside the 'sync' callback to read members
 */
export async function joinPresence(groupId: number, userKey: string) {
  const channel = supabase.channel(`presence:${groupId}`, {
    config: { presence: { key: userKey } },
  });

  channel.on('presence', { event: 'sync' }, () => {
    // Map<userKey, Array<meta>>
    // each meta is what was passed to track()
    const state = channel.presenceState();
    // console.log('presence state', state);
  });

  const status = await channel.subscribe();
  if (status === 'SUBSCRIBED') {
    await channel.track({ at: Date.now() });
  }

  return { channel };
}

/**
 * Broadcast helper (ephemeral, not persisted)
 * Example use: typing indicators, UI pings
 */
export function broadcastTyping(groupId: number, userKey: string) {
  const channel = supabase.channel(`broadcast:${groupId}`);
  channel
    .on('broadcast', { event: 'typing' }, (payload) => {
      // handle inbound typing events elsewhere if needed
      // console.debug('typing:', payload);
    })
    .subscribe()
    .then(() => {
      channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userKey, at: Date.now() },
      });
    });
  return () => supabase.removeChannel(channel);
}

/* ======================================================================================
 * Auth change side-effects (optional, handy for multi-tab + Realtime)
 * ====================================================================================*/

supabase.auth.onAuthStateChange((_event, _session) => {
  // Example: you can refresh presence keys here if you derive them from the user
  // console.log('[auth] event:', _event);
});


export type ContactMessageInsert = {
  name: string;
  email: string;
  message: string;
  user_id?: string | null;
  path?: string | null;
  user_agent?: string | null;
};

export async function insertContactMessage(row: ContactMessageInsert) {
  // Optional: enrich with current user
  const { data: u } = await supabase.auth.getUser();
  const user_id = u?.user?.id ?? null;

  const { error } = await supabase
    .from('contact_messages')
    .insert([{ ...row, user_id }]);

  if (error) throw error;
}
